import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Form, Field } from 'react-final-form';
import { useLocaleFormat } from '@/shared/hooks';
import { Box } from '@/shared/ui/box';
import { Button } from '@/shared/ui/form/button';
import { Image } from '@/shared/ui/image';
import { Condition } from '@/shared/ui/condition';
import { FormField } from '@/shared/ui/form/form-field';
import { CheckboxField } from '@/shared/ui/form/checkbox-field';
import { Link } from '@/shared/ui/link';
import {
  BodyTextBoldSecondary,
  BoldHeadingSenary,
  BodyTextRegularSecondary,
} from '@/shared/ui/typography';
import { useAuth } from '@/features/auth-managment/model/auth-context';
import { useCurrentLocale } from '@locales/client';
import {
  FormWrapper,
  FormTypeSection,
  ResetPasswordBlock,
  ButtonContinue,
  FormTypeSections,
  FormContainer,
  FormChangeSections,
  BoxForSelectLanguage,
} from '@/features/auth-managment/styles';
import { WelcomBannerSignUp } from './ui/welcom-banner-signup';
import { GroupAuthVariant } from './ui/group-auth-variant';
import {
  CasinoBonusBlock,
  ActivationCodeBlock,
  FooterPartnersBlock,
} from './ui/welcom-banner-signup/components';
import { SelectLanguage } from '@/features/select-language';
import { SelectCurrency } from '@/features/select-currency/ui/select-currency';
import { withBreakpointsSlice } from '@/shared/providers/utils/with-breakpoints-slice';
import { useConfirmDataPrompt } from '@/features/data-managment/model/hooks';
import { useDebounce } from '@/shared/hooks';

const Authorization = ({ mobileLandscape }) => {
  const {
    authenticationType,
    authenticationMethod,
    setAuthenticationType,
    setAuthenticationMethod,
    onSubmit,
    form,
    spinner,
    handleResetPassword,
    t,
    PhoneFieldCmp,
    validate,
    handleClose,
    currentStep,
    formWelcomBanner,
    setFormWelcomBanner,
    viewInfo,
    setForm,
  } = useAuth();

  const formatUrl = useLocaleFormat();
  const { addQueryParam } = useConfirmDataPrompt();
  const locale = useCurrentLocale();

  return (
    <Box
      $display="flex"
      $gap={authenticationType === 'signup' ? '10px' : '0'}
      style={{ position: 'relative' }}
      $height={'100%'}
    >
      {!mobileLandscape && (
        <Box
          $width={authenticationType === 'signup' ? '362px' : '0'}
          $transition="width 0.3s ease"
        >
          <AnimatePresence mode="wait">
            {authenticationType === 'signup' && (
              <motion.div
                key="welcome-banner"
                initial={{ x: 100, opacity: 0 }}
                animate={{
                  x: 0,
                  opacity: 1,
                  transition: {
                    type: 'spring',
                    damping: 25,
                    stiffness: 120,
                    duration: 0.4,
                  },
                }}
                exit={{
                  x: 100,
                  opacity: 0,
                  transition: {
                    type: 'spring',
                    damping: 25,
                    stiffness: 120,
                    duration: 0.3,
                  },
                }}
                style={{
                  width: '362px',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  height: '100%',
                }}
              >
                <WelcomBannerSignUp />
              </motion.div>
            )}
          </AnimatePresence>
        </Box>
      )}

      <motion.div
        key={authenticationType}
        initial={{ opacity: 0 }}
        animate={{
          opacity: 1,
          transition: { duration: 0.3 },
        }}
        exit={{
          opacity: 0,
          transition: { duration: 0.2 },
        }}
        style={{
          width: mobileLandscape ? '100%' : '362px',
          position: 'relative',
          marginLeft: authenticationType === 'signup' ? '0' : 'auto',
          marginRight: authenticationType === 'signup' ? '0' : 'auto',
        }}
      >
        <FormWrapper $currentStep={currentStep}>
          <FormTypeSections>
            <FormTypeSection
              $type="signin"
              onClick={() => {
                setAuthenticationType('signin');
                addQueryParam('auth');
              }}
              $isActive={authenticationType === 'signin'}
            >
              <BoldHeadingSenary
                color={authenticationType === 'signin' ? '#FFF' : '#AFAFAF'}
              >
                {t('auth.sign_in')}
              </BoldHeadingSenary>
            </FormTypeSection>
            <FormTypeSection
              $type="signup"
              onClick={() => {
                setAuthenticationType('signup');
                addQueryParam('registration');
              }}
              $isActive={authenticationType === 'signup'}
            >
              <BoldHeadingSenary
                color={authenticationType === 'signup' ? '#FFF' : '#AFAFAF'}
              >
                {t('auth.sign_up')}
              </BoldHeadingSenary>
            </FormTypeSection>
            <FormTypeSection style={{ padding: '12px 16px' }} $type="close">
              <Box onClick={handleClose} $display="flex">
                <Image
                  width={32}
                  height={32}
                  src="/images/icons/close-dialog.svg"
                />
              </Box>
            </FormTypeSection>
          </FormTypeSections>

          <FormContainer
            $withBackground={authenticationType === 'signup'}
            $isSignIn={authenticationType === 'signin'}
            $viewInfo={viewInfo}
          >
            {mobileLandscape && authenticationType === 'signup' && (
              <CasinoBonusBlock bgMod />
            )}
            {((!viewInfo && mobileLandscape) ||
              !mobileLandscape ||
              authenticationType === 'signin') && (
              <>
                <FormChangeSections>
                  <Button
                    onClick={() => setAuthenticationMethod('email')}
                    icon={
                      <Image
                        width={20}
                        height={20}
                        src="/images/icons/email-icon.svg"
                      />
                    }
                    variant={
                      authenticationMethod === 'email'
                        ? 'orangeGradient'
                        : 'transparent'
                    }
                  >
                    <BodyTextBoldSecondary>
                      {t('auth.login.email.button')}
                    </BodyTextBoldSecondary>
                  </Button>

                  <Button
                    style={{
                      opacity: locale == 'ro' ? 0.5 : 1,
                      cursor: locale == 'ro' ? 'not-allowed' : 'pointer',
                    }}
                    disabled={locale == 'ro'}
                    onClick={() => setAuthenticationMethod('phone')}
                    icon={
                      <Image
                        width={20}
                        height={20}
                        src="/images/icons/phone-icon.svg"
                      />
                    }
                    variant={
                      authenticationMethod === 'phone'
                        ? 'orangeGradient'
                        : 'transparent'
                    }
                  >
                    <BodyTextBoldSecondary>
                      {t('auth.login.phone.button')}
                    </BodyTextBoldSecondary>
                  </Button>
                </FormChangeSections>
                <Form
                  onSubmit={onSubmit}
                  validate={validate}
                  initialValues={form}
                  render={({
                    handleSubmit,
                    submitting,
                    pristine,
                    values,
                    ...rest
                  }) => {
                    const valuesWithoutPassword = { ...values };

                    const debouncedValues = useDebounce(
                      JSON.stringify(valuesWithoutPassword),
                      500
                    );

                    useEffect(() => {
                      const formValues = JSON.parse(debouncedValues);
                      setForm(formValues);
                    }, [debouncedValues, setForm]);
                    return (
                      <form onSubmit={handleSubmit}>
                        <Box
                          $display="flex"
                          $flexDirection="column"
                          $gap="16px"
                        >
                          <Condition match={authenticationMethod === 'email'}>
                            <Field
                              name={'email'}
                              autoComplete="off"
                              required
                              placeholder={t('auth.login.email.placeholder')}
                              placeholderColor="#AFAFAF"
                              component={FormField}
                              type={'text'}
                            />
                          </Condition>
                          <Condition match={authenticationMethod === 'phone'}>
                            <Field
                              name={'phone'}
                              required
                              autoComplete="off"
                              component={PhoneFieldCmp}
                              type={'text'}
                              placeholderсolor="#AFAFAF"
                              placeholder={t('auth.login.phone.label')}
                            />
                          </Condition>

                          <Field
                            name="password"
                            id="auth-email-password"
                            placeholderColor="#AFAFAF"
                            resetPasswordSlot={
                              authenticationType === 'signin' ? (
                                <ResetPasswordBlock
                                  onClick={handleResetPassword}
                                >
                                  {t('auth.login.forgot_password')}
                                </ResetPasswordBlock>
                              ) : null
                            }
                            component={FormField}
                            required
                            type="password"
                            placeholder={t('auth.login.password.placeholder')}
                            label={
                              <BodyTextBoldSecondary></BodyTextBoldSecondary>
                            }
                          />

                          <Condition match={authenticationType === 'signup'}>
                            <Box
                              $display="flex"
                              $alignItems="center"
                              $justifyContent="space-between"
                              $gap="12px"
                            >
                              <BoxForSelectLanguage>
                                <SelectLanguage authMode />
                              </BoxForSelectLanguage>

                              <Box $flex="1">
                                <SelectCurrency
                                  authMode={{
                                    formWelcomBanner,
                                    setFormWelcomBanner: setFormWelcomBanner,
                                    authModeActive: true,
                                  }}
                                />
                              </Box>
                            </Box>
                            {mobileLandscape && <ActivationCodeBlock bgMod />}
                          </Condition>
                        </Box>

                        <Condition match={authenticationType === 'signup'}>
                          <Box $paddingTop={24}>
                            <Field
                              name="isLegalAge"
                              component={CheckboxField}
                              type="checkbox"
                              label={
                                <BodyTextRegularSecondary>
                                  {t('auth.login.legal_age_checkbox')}.{' '}
                                  <Link
                                    target="_blank"
                                    href={formatUrl('/info?id=ua')}
                                  >
                                    <span style={{ color: '#FF6601' }}>
                                      {t('auth.login.terms')}
                                    </span>
                                  </Link>
                                </BodyTextRegularSecondary>
                              }
                            />
                          </Box>
                          <Box $paddingTop={12}>
                            <Field
                              name="isNews"
                              component={CheckboxField}
                              type="checkbox"
                              label={
                                <BodyTextRegularSecondary>
                                  {t('auth.login.news_checkbox')}
                                </BodyTextRegularSecondary>
                              }
                            />
                          </Box>
                        </Condition>
                        <Box $paddingTop={16}>
                          <ButtonContinue
                            type="submit"
                            disabled={rest.invalid}
                            isLoading={spinner}
                          >
                            <BodyTextBoldSecondary>
                              {t('auth.login.submit')}
                            </BodyTextBoldSecondary>
                          </ButtonContinue>
                        </Box>
                        <GroupAuthVariant />
                      </form>
                    );
                  }}
                />
                {authenticationType === 'signup' && mobileLandscape && (
                  <FooterPartnersBlock />
                )}
              </>
            )}
          </FormContainer>
        </FormWrapper>
      </motion.div>
    </Box>
  );
};

const AuthorizationWithBreakpointSlice = withBreakpointsSlice(
  Authorization,
  ({ mobileLandscape }) => ({ mobileLandscape })
);

export { AuthorizationWithBreakpointSlice as Authorization };
